import { useTranslate } from "ra-core";
import { Link } from "react-router";

import type { CollectSummary } from "./homeModel";
import { useHomeFormat } from "./useHomeFormat";

const SEGMENTS = [
  { key: "overdue", count: "overdueCount", tone: "overdue" },
  { key: "dueThisWeek", count: "dueThisWeekCount", tone: "week" },
  { key: "later", count: "laterCount", tone: "later" },
] as const;

/**
 * «Por cobrar»: outstanding invoices (VAT included) as one stacked bar:
 * overdue, due within 7 days, later. Status colors always come with a
 * labelled legend (and the amounts), never color alone.
 */
export const CollectCard = ({
  collect,
  compact = false,
}: {
  collect: CollectSummary;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.collect.${key}`, options);
  const segments = SEGMENTS.filter((segment) => collect[segment.key] > 0);
  const label = segments
    .map(
      (segment) => `${t(segment.key)}: ${format.money(collect[segment.key])}`,
    )
    .join(", ");

  return (
    <section
      className="home-card home-collect"
      data-compact={compact || undefined}
      aria-labelledby={compact ? undefined : "home-collect-title"}
      aria-label={compact ? t("title") : undefined}
    >
      {compact ? null : (
        <header className="home-card__head">
          <h2 id="home-collect-title" className="home-card__title">
            {t("title")}
            <span className="home-card__subtitle">{t("vat")}</span>
          </h2>
          {collect.collectedThisMonth > 0 ? (
            <span className="home-badge" data-tone="positive">
              {t("collected_month", {
                amount: format.money(collect.collectedThisMonth),
              })}
            </span>
          ) : null}
        </header>
      )}
      <div className="home-collect__total">
        <span className="home-collect__amount">
          {format.money(collect.total)}
        </span>
        {compact && collect.overdue > 0 ? (
          <span className="home-badge" data-tone="negative">
            {t("overdue_badge", { amount: format.money(collect.overdue) })}
          </span>
        ) : null}
      </div>
      {collect.total > 0 ? (
        <>
          <div className="home-stack" role="img" aria-label={label}>
            {segments.map((segment) => (
              <span
                key={segment.key}
                data-tone={segment.tone}
                style={{ flexGrow: collect[segment.key] }}
                title={`${t(segment.key)}: ${format.money(collect[segment.key])}`}
              />
            ))}
          </div>
          {compact ? null : (
            <ul className="home-legend">
              {SEGMENTS.map((segment) => (
                <li key={segment.key}>
                  <span
                    className="home-legend__dot"
                    data-tone={segment.tone}
                    aria-hidden
                  />
                  <span className="home-legend__label">
                    {t(segment.key)} ({collect[segment.count]})
                  </span>
                  <span className="home-legend__value">
                    {format.money(collect[segment.key])}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p className="home-collect__empty">{t("empty")}</p>
      )}
      {compact ? null : (
        <Link to="/invoices" className="home-link home-collect__link">
          {t("see_all")}
        </Link>
      )}
    </section>
  );
};
